import { useMemo } from 'react';

import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import type { Estate } from '@/features/estates-property/api/estate';
import type { Project } from '@/features/projects/api/projects';
import type { QueryConfig } from '@/lib/react-query';
import type { LoaderProps } from '@/lib/router';
import { ApiError, api, extractData } from '@/lib/treaty';
import { groupBy } from '@/utils/groupBy';
import { uniqBy } from '@/utils/uniqBy';

export type MediaQueryOptions = {
  queryConfig?: QueryConfig<typeof getMedia>;
  id: Estate['id'];
};

export type MediaByIdQueryOptions = {
  queryConfig?: QueryConfig<typeof getMedia>;
  id: Estate['id'];
  mediaId: string;
};

export const updateMediaImageName = (id: Estate['id'], imageId: string, name: string) => {
  return extractData(api.estates({ id }).images({ imageId: imageId }).patch({ name }));
};

export const getMedia = (id: Estate['id']) => {
  return extractData(api.estates({ id }).media.get());
};

export const getMediaById = (id: Estate['id'], mediaId: string) => {
  return extractData(api.estates({ id }).media({ mediaId }).get());
};

export const getMediaOptions = (id: Estate['id']) => ({
  queryKey: ['media', id],
  queryFn: () => getMedia(id),
});

export const getMediaByIdOptions = (id: Estate['id'], mediaId: string) => ({
  queryKey: ['media', id, mediaId],
  queryFn: () => getMediaById(id, mediaId),
});

export const getMediaQueryOptions = (id: Estate['id']) => {
  return queryOptions(getMediaOptions(id));
};

export const getMediaByIdQueryOptions = (id: Estate['id'], mediaId: string) => {
  return queryOptions(getMediaByIdOptions(id, mediaId));
};

export const ensureMediaQueryData = ({ context: { queryClient }, params }: LoaderProps) => {
  return queryClient.ensureQueryData(getMediaQueryOptions(params.estateId));
};

export const ensureMediaByIdQueryData = ({ context: { queryClient }, params }: LoaderProps) => {
  return queryClient.ensureQueryData(getMediaByIdQueryOptions(params.estateId, params.mediaId));
};

export const useMedia = ({ queryConfig, id }: MediaQueryOptions) => {
  return useSuspenseQuery({ ...queryConfig, ...getMediaQueryOptions(id) });
};

export const useMediaGetById = ({ queryConfig, id, mediaId }: MediaByIdQueryOptions) => {
  return useSuspenseQuery({ ...queryConfig, ...getMediaByIdQueryOptions(id, mediaId) });
};

export type MediaResponseType = NonNullable<
  Awaited<ReturnType<ReturnType<typeof api.estates>['media']['get']>>['data']
>;

export type DigitalTwinsType = MediaResponseType['digitalTwins'];
export type ImagesType = MediaResponseType['images'];
export type SphericalImagesType = MediaResponseType['sphericalImages'];
export type FloorPlans = MediaResponseType['floorLayouts'];
export type FocusPointsType = MediaResponseType['pois'];

// --------- project media ----------

export const getMediaProjectById = (id: Project['id'], mediaId: string) => {
  return extractData(api.projects({ id }).media({ mediaId }).get());
};

export const getMediaProjectByIdOptions = (id: Project['id'], mediaId: string) => ({
  queryKey: ['media', id, mediaId],
  queryFn: () => getMediaProjectById(id, mediaId),
});

export const ensureMediaProjectByIdQueryData = ({
  context: { queryClient },
  params,
}: LoaderProps) => {
  return queryClient.ensureQueryData(getMediaProjectByIdOptions(params.projectId, params.mediaId));
};

export const getMediaProjectByIdQueryOptions = (id: Project['id'], mediaId: string) => {
  return queryOptions(getMediaProjectByIdOptions(id, mediaId));
};

export const useMediaProjectGetById = ({
  queryConfig,
  id,
  mediaId,
}: {
  queryConfig?: QueryConfig<typeof getMedia>;
  id: Project['id'];
  mediaId: string;
}) => {
  return useSuspenseQuery({ ...queryConfig, ...getMediaProjectByIdQueryOptions(id, mediaId) });
};

export const updateMediaProjectImageName = (id: Project['id'], imageId: string, name: string) => {
  return extractData(api.projects({ id }).images({ imageId: imageId }).patch({ name }));
};

export type ProjectMediaQueryOptions = {
  queryConfig?: QueryConfig<typeof getProjectMedia>;
  id?: Project['id'] | null;
  // set when an estate page shows its project's media
  forEstate?: boolean;
};

export type ProjectMediaResponseType = NonNullable<
  Awaited<ReturnType<ReturnType<typeof api.projects>['media']['get']>>['data']
>;

//updatedAt, deletedAt, fileTypets
export type ProjectImageType = ProjectMediaResponseType['images'][number];
export type ProjectLayoutType = ProjectMediaResponseType['layouts'][number];

export const getProjectMedia = (id: Project['id']) => {
  return extractData(api.projects({ id }).media.get());
};

const emptyProjectMedia: ProjectMediaResponseType = {
  images: [],
  sphericalImages: [],
  layouts: [],
  metadata: {
    permissions: {
      general: 'none',
      media: 'none',
      documentsInternal: 'none',
      documentsExternal: 'none',
    },
  },
};

export const getProjectMediaOptions = (id?: Project['id'] | null) => ({
  queryKey: ['media', id],
  queryFn: () => (id ? getProjectMedia(id) : Promise.resolve(emptyProjectMedia)),
});

// An estate can grant media access without access to its project's media.
// Estate media pages then show only the estate's own media. The separate cache key
// keeps this empty fallback away from the project's own media pages.
export const getEstateProjectMediaOptions = (id?: Project['id'] | null) => ({
  queryKey: ['media', id, 'estate'],
  queryFn: async () => {
    if (!id) return emptyProjectMedia;

    try {
      return await getProjectMedia(id);
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return emptyProjectMedia;
      }

      throw error;
    }
  },
});

export const getProjectMediaQueryOptions = (id?: Project['id'] | null) => {
  return queryOptions(getProjectMediaOptions(id));
};

export const ensureProjectMediaQueryData = ({ context: { queryClient }, params }: LoaderProps) => {
  return queryClient.ensureQueryData(getProjectMediaQueryOptions(params.projectId));
};

export const useProjectMedia = ({ queryConfig, id, forEstate }: ProjectMediaQueryOptions) => {
  const { data: projectMedia, isPending } = useSuspenseQuery({
    ...queryConfig,
    ...queryOptions(forEstate ? getEstateProjectMediaOptions(id) : getProjectMediaOptions(id)),
  });
  const data = useMemo(() => {
    // group a relation by day, like { "2024-11-23": [], "2024-11-22": [] }
    // Project media does not show media from Estates => filter out media with estateId
    const groupByDay = <T extends { id: string; createdAt: string; estateId: string | null }>(
      items: T[] = []
    ) =>
      groupBy(
        (item) => dayjs(item.createdAt).format('YYYY-MM-DD'),
        uniqBy(
          items.filter((media) => !media.estateId),
          'id'
        )
      );

    // typed per relation, layouts carry fileType and images do not
    return {
      images: groupByDay<ProjectImageType>(projectMedia?.images),
      layouts: groupByDay<ProjectLayoutType>(projectMedia?.layouts),
    };
  }, [projectMedia]);
  return { data, isPending };
};
